import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper, Typography, Switch, Tooltip, IconButton, Fab, Box, Button, Modal,
    Chip, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useAuth } from '../context/AuthContext';
import { useSmartAlerts, useToggleSmartAlert, useDeleteSmartAlert } from '../hooks/useSmartAlerts';
import { useNotification } from '../context/NotificationContext';

const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
        return new Date(isoString).toLocaleString('hr-HR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return isoString;
    }
};

const sortRows = (rows, key, dir) =>
    [...rows].sort((a, b) => {
        let av, bv;
        switch (key) {
            case 'name': av = a.name ?? ''; bv = b.name ?? ''; break;
            case 'terms': av = (a.terms?.length ?? 0); bv = (b.terms?.length ?? 0); break;
            case 'last_checked': av = a.last_checked ?? ''; bv = b.last_checked ?? ''; break;
            case 'is_active': av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; break;
            default: av = ''; bv = '';
        }
        if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return dir === 'asc' ? cmp : -cmp;
    });

const toggleDir = (prev, key) => ({
    key,
    dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
});

export default function SmartAlerts() {
    const { user } = useAuth();
    const { data, isLoading } = useSmartAlerts();
    const smartAlerts = data?.smart_alerts ?? [];
    const navigate = useNavigate();
    const toggleSmartAlert = useToggleSmartAlert();
    const deleteSmartAlert = useDeleteSmartAlert();
    const { showSuccess, showError } = useNotification();

    const limit = user?.smart_alert_limit ?? 0;

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

    useEffect(() => { document.title = 'Smart Alerti | Media Scanner'; }, []);

    const handleToggle = (alert) => {
        toggleSmartAlert.mutate(
            { id: alert.id, is_active: !alert.is_active },
            {
                onSuccess: () => showSuccess(alert.is_active ? 'Smart alert deaktiviran' : 'Smart alert aktiviran'),
                onError: () => showError('Greška pri promjeni statusa'),
            }
        );
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        deleteSmartAlert.mutate(
            { id: deleteTarget.id, is_active: deleteTarget.is_active },
            {
                onSuccess: () => {
                    setConfirmDeleteOpen(false);
                    setDeleteTarget(null);
                    showSuccess('Smart alert obrisan');
                },
                onError: () => showError('Greška pri brisanju'),
            }
        );
    };

    const sortProps = (key) => ({
        active: sort.key === key,
        direction: sort.key === key ? sort.dir : 'asc',
        onClick: () => setSort(prev => toggleDir(prev, key)),
    });

    const sorted = sortRows(smartAlerts, sort.key, sort.dir);

    if (isLoading) return null;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                        Smart Alerti
                    </Typography>
                    <Chip
                        label={`${smartAlerts.length} / ${limit}`}
                        size="small"
                        sx={{ fontWeight: 600, backgroundColor: '#e3f2fd', color: '#0079bf' }}
                    />
                </Box>

                {smartAlerts.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <NotificationsOffIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                            Nema postavljenih smart alerta.
                        </Typography>
                        {smartAlerts.length < limit && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{ mt: 2 }}
                                onClick={() => navigate('/smartAlertCreate')}
                            >
                                Kreiraj prvi smart alert
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <Paper sx={{ overflow: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <TableSortLabel {...sortProps('name')}>Naziv</TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <TableSortLabel {...sortProps('terms')}>Pojmovi</TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <TableSortLabel {...sortProps('last_checked')}>Zadnja provjera</TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <TableSortLabel {...sortProps('is_active')}>Status</TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Akcije</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sorted.map((alert) => (
                                    <TableRow key={alert.id} hover>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600, color: '#0079bf', cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                                onClick={() => navigate(`/smart-alert-details/${alert.id}`)}
                                            >
                                                {alert.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: '#555', maxWidth: 250 }}>
                                            {alert.terms?.length ? alert.terms.map(t => t.name).join(', ') : '—'}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                                            {formatDate(alert.last_checked)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                                        backgroundColor: alert.is_active ? '#e8f5e9' : '#f5f5f5',
                                                        color: alert.is_active ? '#27ae60' : '#999',
                                                    }}
                                                >
                                                    {alert.is_active ? 'Aktivan' : 'Neaktivan'}
                                                </Typography>
                                                <Tooltip title={alert.is_active ? 'Deaktiviraj' : 'Aktiviraj'}>
                                                    <Switch
                                                        size="small"
                                                        checked={alert.is_active}
                                                        onChange={() => handleToggle(alert)}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Uredi">
                                                <IconButton size="small" onClick={() => navigate(`/smart-alert-details/${alert.id}`)}>
                                                    <EditNoteIcon sx={{ color: '#0079bf', fontSize: 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Obriši">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setDeleteTarget(alert); setConfirmDeleteOpen(true); }}
                                                >
                                                    <DeleteIcon sx={{ color: '#e74c3c', fontSize: 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Box>

            {smartAlerts.length < limit && (
                <Fab
                    color="primary"
                    aria-label="Dodaj smart alert"
                    sx={{ position: 'fixed', bottom: 32, right: 32, boxShadow: 3, zIndex: 1000 }}
                    onClick={() => navigate('/smartAlertCreate')}
                >
                    <AddIcon />
                </Fab>
            )}

            <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
                <Paper
                    elevation={8}
                    sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)', width: 420, borderRadius: 2, p: 4,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
                        Brisanje smart alerta
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Jeste li sigurni da želite obrisati smart alert "{deleteTarget?.name}"? Ova akcija se ne može poništiti.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" onClick={() => { setConfirmDeleteOpen(false); setDeleteTarget(null); }}>
                            Odustani
                        </Button>
                        <Button variant="contained" color="error" onClick={handleDelete}>
                            Izbriši
                        </Button>
                    </Box>
                </Paper>
            </Modal>
        </Box>
    );
}
